import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
	ArrowLeft,
	Coffee,
	Plus,
	Minus,
	Trash2,
	ShoppingCart,
} from "lucide-react";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function POSCheckout() {
	const [categories, setCategories] = useState([]);
	const [products, setProducts] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState("all");

	const [cart, setCart] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	useEffect(() => {
		const controller = new AbortController();

		async function loadProducts() {
			try {
				const response = await fetch(`${API_URL}/api/pos/catalog`, {
					signal: controller.signal,
				});

				if (!response.ok) {
					throw new Error(`Erreur API : ${response.status}`);
				}

				const data = await response.json();

				setCategories(data.categories || []);
				setProducts(data.products || []);
			} catch (err) {
				if (err.name !== "AbortError") {
					setError(err.message);
				}
			} finally {
				setLoading(false);
			}
		}

		loadProducts();

		return () => controller.abort();
	}, []);

	const formatCHF = (value) =>
		new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(Number(value));

	const filteredProducts =
		selectedCategory === "all"
			? products
			: products.filter((product) => product.category_id === selectedCategory);

	function addToCart(product, variant = null) {
		const id = variant ? `${product.id}-${variant.id}` : product.id;

		const price = Number(variant ? variant.price : product.price);

		setCart((previous) => {
			const existing = previous.find((item) => item.id === id);

			if (existing) {
				return previous.map((item) =>
					item.id === id ? { ...item, quantity: item.quantity + 1 } : item,
				);
			}

			return [
				...previous,
				{
					id,
					name: product.name,
					variant: variant?.name || null,
					price,
					quantity: 1,
				},
			];
		});
	}

	function updateQuantity(id, change) {
		setCart((previous) =>
			previous
				.map((item) =>
					item.id === id
						? {
								...item,
								quantity: item.quantity + change,
							}
						: item,
				)
				.filter((item) => item.quantity > 0),
		);
	}

	const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

	return (
		<div className="min-h-screen bg-[#F7F3ED] text-[#34251D]">
			<header className="flex flex-wrap items-center justify-between gap-4 bg-[#34251D] px-6 py-5 text-white">
				<div className="flex items-center gap-3">
					<Coffee size={28} />
					<div>
						<h1 className="text-xl font-bold">Léger's Coffee</h1>
						<p className="text-xs text-white/70">
							Point de vente — Mode préparation
						</p>
					</div>
				</div>

				<Link
					to="/pos"
					className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2"
				>
					<ArrowLeft size={18} />
					Tableau de bord
				</Link>
			</header>

			<div className="grid min-h-[calc(100vh-90px)] grid-cols-1 lg:grid-cols-[1fr_380px]">
				<main className="p-5">
					<div className="mb-6 flex flex-wrap gap-2">
						<button
							onClick={() => setSelectedCategory("all")}
							className="rounded-xl bg-white px-4 py-3 shadow-sm"
						>
							Tous
						</button>

						{categories.map((category) => (
							<button
								key={category.id}
								onClick={() => setSelectedCategory(category.id)}
								className="rounded-xl bg-white px-4 py-3 shadow-sm"
							>
								{category.name}
							</button>
						))}
					</div>

					{loading && <p>Chargement des produits...</p>}

					{error && <p className="text-red-600">{error}</p>}

					<div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
						{filteredProducts.map((product) => (
							<article
								key={product.id}
								className="overflow-hidden rounded-2xl bg-white shadow-sm"
							>
								{product.image_url && (
									<img
										src={product.image_url}
										alt={product.name}
										className="aspect-square w-full object-contain"
									/>
								)}

								<div className="p-4">
									<h3 className="font-semibold">{product.name}</h3>

									{product.has_variants ? (
										<div className="mt-3 space-y-2">
											{(product.variants || []).map((variant) => (
												<button
													key={variant.id}
													onClick={() => addToCart(product, variant)}
													className="w-full rounded-lg border p-2 text-left text-sm"
												>
													{variant.name}
													<span className="block font-semibold">
														{formatCHF(variant.price)}
													</span>
												</button>
											))}
										</div>
									) : (
										<button
											onClick={() => addToCart(product)}
											className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#34251D] p-3 text-white"
										>
											<Plus size={17} />
											{formatCHF(product.price)}
										</button>
									)}
								</div>
							</article>
						))}
					</div>
				</main>

				<aside className="border-l bg-white p-5">
					<div className="mb-6 flex items-center gap-3">
						<ShoppingCart size={24} />
						<h2 className="text-xl font-bold">Panier</h2>
					</div>

					{cart.length === 0 ? (
						<p className="text-gray-500">Aucun produit ajouté.</p>
					) : (
						<div className="space-y-4">
							{cart.map((item) => (
								<div key={item.id} className="border-b pb-4">
									<div className="flex justify-between gap-3">
										<div>
											<p className="font-semibold">{item.name}</p>

											{item.variant && (
												<p className="text-sm text-gray-500">{item.variant}</p>
											)}
										</div>

										<button
											onClick={() =>
												setCart((previous) =>
													previous.filter((p) => p.id !== item.id),
												)
											}
										>
											<Trash2 size={18} className="text-red-500" />
										</button>
									</div>

									<div className="mt-3 flex items-center justify-between">
										<div className="flex items-center gap-3">
											<button onClick={() => updateQuantity(item.id, -1)}>
												<Minus size={18} />
											</button>

											<span>{item.quantity}</span>

											<button onClick={() => updateQuantity(item.id, 1)}>
												<Plus size={18} />
											</button>
										</div>

										<span className="font-semibold">
											{formatCHF(item.price * item.quantity)}
										</span>
									</div>
								</div>
							))}
						</div>
					)}

					<div className="mt-8 border-t pt-5">
						<div className="flex justify-between text-xl font-bold">
							<span>Total indicatif</span>
							<span>{formatCHF(total)}</span>
						</div>

						<button
							disabled
							className="mt-5 w-full cursor-not-allowed rounded-xl bg-gray-300 p-4 font-semibold text-gray-600"
						>
							Encaissement indisponible
						</button>

						<p className="mt-3 text-xs text-gray-500">
							Cette version permet de préparer un panier. Aucun paiement ni
							commande n'est enregistré.
						</p>
					</div>
				</aside>
			</div>
		</div>
	);
}
